import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import certificateService from '../api/services/certificateService';
import { Button, ThemedText, ActionMenu, ConfirmDialog } from '../components';
import { PageContainer, PageHeaderRow as PageHeader } from '../components/PageStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { SearchContainer, StyledSearchContainer, SearchIcon, TransparentInput } from '../components/SearchStyles';
import { formatDateOnly } from '../utils/dateUtils';
import { Search } from 'lucide-react';

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: 18px;
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  font-size: 14px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.tint}20;
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.lg};
`;

export const CertificateDefinitionsSection = () => {
  const theme = useTheme();
  const { isAdmin } = useRole();
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDef, setEditingDef] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      const response = await certificateService.getDefinitions();
      setDefinitions(Array.isArray(response) ? response : (response?.data || []));
    } catch (error) {
      console.error('Failed to fetch certificate definitions:', error);
      toast.error('Failed to load certificate definitions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingDef(null);
    setFormName('');
    setModalOpen(true);
  };

  const handleOpenEdit = (def) => {
    setEditingDef(def);
    setFormName(def.name);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      setSaving(true);
      if (editingDef) {
        await certificateService.updateDefinition(editingDef.id, { name: formName.trim() });
        toast.success('Certificate type updated');
      } else {
        await certificateService.createDefinition({ name: formName.trim() });
        toast.success('Certificate type created');
      }
      setModalOpen(false);
      setEditingDef(null);
      setFormName('');
      fetchDefinitions();
    } catch (error) {
      console.error('Failed to save certificate definition:', error);
      toast.error(error.response?.data?.message || 'Failed to save certificate type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await certificateService.deleteDefinition(id);
      toast.success('Certificate type deleted');
      setDeleteConfirmId(null);
      fetchDefinitions();
    } catch (error) {
      console.error('Failed to delete certificate definition:', error);
      toast.error(error.response?.data?.message || 'Failed to delete certificate type');
    }
  };

  const formatDate = (dateString) => {
    return formatDateOnly(dateString);
  };

  const filteredDefinitions = definitions.filter(def =>
    def.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <div>
            <ThemedText type="title">Certificates</ThemedText>
            <ThemedText type="subtitle">Manage certificate types required for work orders</ThemedText>
          </div>
          {isAdmin() && (
            <Button onClick={handleOpenCreate}>
              + New Certificate Type
            </Button>
          )}
        </PageHeader>

        <SearchContainer theme={theme}>
          <StyledSearchContainer theme={theme}>
            <SearchIcon><Search size={16} /></SearchIcon>
            <TransparentInput
              placeholder="Search certificate types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={theme}
            />
          </StyledSearchContainer>
        </SearchContainer>

        <TableContainer theme={theme}>
          {loading ? (
            <LoadingState theme={theme}>
              <ThemedText>Loading certificate types...</ThemedText>
            </LoadingState>
          ) : filteredDefinitions.length === 0 ? (
            <EmptyState theme={theme}>
              <ThemedText>{searchTerm ? 'No matching certificate types found' : 'No certificate types defined yet'}</ThemedText>
            </EmptyState>
          ) : (
            <Table>
              <Thead theme={theme}>
                <tr>
                  <Th theme={theme}>Name</Th>
                  <Th theme={theme}>Created</Th>
                  <Th theme={theme}>Last Updated</Th>
                  {isAdmin() && <Th theme={theme} style={{ width: '50px' }}></Th>}
                </tr>
              </Thead>
              <Tbody>
                {filteredDefinitions.map(def => (
                  <Tr key={def.id} theme={theme}>
                    <Td theme={theme}>
                      <ThemedText type="defaultSemiBold">{def.name}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{formatDate(def.createdAt)}</ThemedText>
                    </Td>
                    <Td theme={theme}>
                      <ThemedText type="small">{def.updatedAt ? formatDate(def.updatedAt) : '-'}</ThemedText>
                    </Td>
                    {isAdmin() && (
                      <Td theme={theme}>
                        <ActionMenu actions={[
                          { label: 'Edit', onClick: () => handleOpenEdit(def) },
                          { label: 'Delete', onClick: () => setDeleteConfirmId(def.id), variant: 'danger' },
                        ]} />
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </TableContainer>

        {/* Create/Edit Modal */}
        {modalOpen && (
          <ModalOverlay onClick={() => setModalOpen(false)}>
            <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
              <ModalTitle theme={theme}>
                {editingDef ? 'Edit Certificate Type' : 'New Certificate Type'}
              </ModalTitle>
              <FormGroup theme={theme}>
                <Label theme={theme}>Name</Label>
                <StyledInput
                  theme={theme}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., First Aid, Forklift License"
                  maxLength={200}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                />
              </FormGroup>
              <ModalActions theme={theme}>
                <Button variant="secondary" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving || !formName.trim()}>
                  {saving ? 'Saving...' : editingDef ? 'Update' : 'Create'}
                </Button>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        <ConfirmDialog
          isOpen={deleteConfirmId !== null}
          title="Delete Certificate Type"
          message="Are you sure you want to delete this certificate type? This will not affect existing user certificates."
          onConfirm={() => handleDelete(deleteConfirmId)}
          onCancel={() => setDeleteConfirmId(null)}
        />
    </PageContainer>
  );
};

export const CertificateDefinitionsPage = () => (
  <Layout currentPath="/settings">
    <CertificateDefinitionsSection />
  </Layout>
);

export default CertificateDefinitionsPage;
